import { BybSection } from "@Components/common/byb-ds";
import Icon from "@Components/generic/icon";
import formatMetricScoreType from "@Containers/app/stat-cards/tooltips/format-metric-type";
import OptionsDrawer from "@Containers/shared/cards/components/options-drawer";
import DateRange from "@Enums/DateRange";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import type { LongTermResultPoint } from "@Helpers/types";
import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import useI18n from "@i18n";
import { Button, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { isNumber } from "lodash-es";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface IOverallProgressSection {
  byb_iq: number;
  longTermResults: LongTermResultPoint[];
  dateRange: string;
  optionsAnchorElement: any;
  setOptionsAnchorElement: (element: any) => void;
  setDateRange: (dateRange: DateRange) => void;
  loading?: boolean;
}

const OverallProgressSection = ({
  byb_iq,
  longTermResults,
  dateRange,
  optionsAnchorElement,
  setOptionsAnchorElement,
  setDateRange,
  loading = false,
}: IOverallProgressSection) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const color = theme.palette.dsPrimary.v300;

  return (
    <BybSection sx={{ padding: 2 }}>
      <Stack alignItems="center" spacing={4}>
        <Stack width="100%" boxSizing={"border-box"} spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="textS600" color={theme.palette.text.secondary}>
              {i18n(`progress.section.progress.${dateRange.toLowerCase()}`)}
            </Typography>
            <Button
              size="small"
              sx={{
                color: theme.palette.dsNeutral.v200,
                minWidth: theme.spacing(0),
              }}
              onClick={e => setOptionsAnchorElement(e.currentTarget)}
            >
              <Icon
                icon={faEllipsis}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: theme.spacing(2.5),
                  width: theme.spacing(2.5),
                  fontSize: theme.spacing(2.5),
                }}
              />
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="baseline">
             {!isNumber(byb_iq) || byb_iq < 0 ? (
              <Skeleton variant="rounded" width={60} height={30} />
            ) : (
              <Typography variant="text3xl700" color={theme.palette.text.primary}>
                {formatMetricScoreType(MetricFilterTypes.BYB_IQ, byb_iq)}
              </Typography>
            )}
            <Typography variant="textBase700" color={theme.palette.text.primary}>
              {i18n(`metric.byb_iq.name`)}
            </Typography>
          </Stack>
        </Stack>
         {isNumber(byb_iq) && byb_iq >= 0 ? (
          longTermResults.length > 0 ? (
            <Stack width="100%" height="100%" position="relative" right={20} boxSizing="border-box">
              <ResponsiveContainer height={250} width="100%" className="swiper-no-swiping">
                <AreaChart data={longTermResults}>
                  <XAxis dataKey="name" fontSize={theme.spacing(1.5)} interval={0} stroke={theme.palette.dsNeutral.v300} tickLine={false} axisLine={false} />
                  <YAxis fontSize={theme.spacing(1.5)} stroke={theme.palette.dsNeutral.v300} tickLine={false} axisLine={false} />
                  <defs>
                    <linearGradient id="metric-fill-positive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="score" stroke={color} strokeWidth={2} fill="url(#metric-fill-positive)" connectNulls={true} />
                  <Tooltip
                    wrapperStyle={{
                      outline: "none",
                    }}
                    formatter={value => [Number(value).toFixed(0)]}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Stack>
          ) : (
            <Stack width="100%" height={250} alignItems="center" justifyContent="center">
              <Typography variant="textBase500" color={theme.palette.text.secondary}>
                {i18n(`progress.section.no-data`)}
              </Typography>
            </Stack>
          )
        ) : (
          <Skeleton variant="rounded" width="100%" height={250} />
        )}
      </Stack>

      <OptionsDrawer
        open={Boolean(optionsAnchorElement)}
        options={Object.values(DateRange)}
        onClose={() => {
          setOptionsAnchorElement(null);
        }}
        labelI18nPrefix="progress.section.progress."
        selectedOption={dateRange}
        onOptionSelected={(option: string) => {
          setDateRange(option as DateRange);
        }}
      />
    </BybSection>
  );
};

export default OverallProgressSection;
